import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

// Components
import ActivityItemCard from './ActivityItemCard'
import Popover from './Popover'
import Switch from './formControl/Switch'
import Text from './Text'
import { NavTab, NavTabs } from './Tabs'

// Utils
import useActivityFeed from '../hooks/useActivityFeed'

// Styles
import { hsla } from '../styles/colors'

// Types
import { IActivityItem } from '../utils/activityFeed'

type ActivityTab = 'requests' | 'activity'

interface IProps {
  anchor: HTMLElement | null
  isOpen: boolean
  onClose: () => void
  onSeen: (notificationIds: string[]) => void
}

const NotificationsPopover: React.FC<IProps> = ({
  anchor,
  isOpen,
  onClose,
  onSeen,
}) => {
  const { t } = useTranslation()
  const {
    requests,
    completedRequests,
    activity,
    openRequestCount,
    hasUnreadActivity,
  } = useActivityFeed()
  const [tab, setTab] = useState<ActivityTab>('activity')
  const [showCompleted, setShowCompleted] = useState(false)

  // The bell badge counts open requests, so opening lands on whatever the badge
  // was about. Reset on every open rather than remembering the last tab.
  useEffect(() => {
    if (isOpen) {
      setTab(openRequestCount > 0 ? 'requests' : 'activity')
      setShowCompleted(false)
    }
    // Deliberately keyed on isOpen alone: the tab is chosen when the popover
    // opens and must not jump if the request count changes while it is open.
  }, [isOpen])

  // Finished rows sit below the open ones: what still needs doing stays on top.
  const requestRows = showCompleted
    ? [...requests, ...completedRequests]
    : requests
  const showCompletedLabel = `${t('general.actions.show')} ${t(
    'general.states.completed'
  ).toLowerCase()}`

  const renderItem = (item: IActivityItem) => (
    <ActivityItemCard
      key={item.id}
      item={item}
      onNavigate={onClose}
      onSeen={onSeen}
    />
  )

  const renderEmpty = (message: string) => (
    <EmptyState>
      <Text textStyle="label" color="textLight" textAlign="center">
        {message}
      </Text>
    </EmptyState>
  )

  return (
    <Popover
      anchor={anchor}
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-end"
      // Clear of the nav bar's own edge: at 8 the panel sat on the seam.
      offset={16}
      initialFocus="container"
      ariaLabel={t('emails.summary.activity')}
    >
      <Container>
        <Header>
          <NavTabs>
            <NavTab
              label={t('general.requests')}
              tagAmount={openRequestCount || undefined}
              selected={tab === 'requests'}
              onClick={() => setTab('requests')}
            />
            <NavTab
              label={t('emails.summary.activity')}
              // The panel opens on Requests when work is waiting, so the other tab
              // has to say for itself that there is something new behind it.
              showDot={hasUnreadActivity}
              selected={tab === 'activity'}
              onClick={() => setTab('activity')}
            />
          </NavTabs>

          {/* Pinned above the scroller: a filter that scrolls away is a filter
              nobody finds once the list is long. */}
          {tab === 'requests' && completedRequests.length > 0 && (
            <ToggleRow>
              <Text textStyle="caption" color="textLight">
                {showCompletedLabel}
              </Text>
              <Switch
                isOn={showCompleted}
                size="small"
                onToggle={() => setShowCompleted(!showCompleted)}
                ariaLabel={showCompletedLabel}
              />
            </ToggleRow>
          )}
        </Header>

        <Scroller>
          {tab === 'requests' &&
            (requestRows.length === 0
              ? renderEmpty(t('notifications.general.noRequests'))
              : requestRows.map(renderItem))}

          {tab === 'activity' &&
            (activity.length === 0
              ? renderEmpty(t('notifications.general.noActivity'))
              : activity.map((section) => (
                  <section key={section.group}>
                    <GroupHeading>
                      <Text textStyle="labelSmallCaps" color="textLight">
                        {t(`notifications.general.${section.group}`)}
                      </Text>
                    </GroupHeading>
                    {section.items.map(renderItem)}
                  </section>
                )))}
        </Scroller>
      </Container>
    </Popover>
  )
}

export default NotificationsPopover

const Container = styled.div`
  width: 420px;
  max-width: calc(100vw - 32px);
  display: flex;
  flex-direction: column;
`

const Header = styled.div`
  /* No border of its own: the tab strip's underline is the divider. No padding
     above it either — the tabs bring their own, and stacking the two put more air
     over the labels than under them. */
  padding: 0 20px;
`

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0 6px;
`

const Scroller = styled.div`
  /* Bounded by the viewport too: a fixed height runs off short windows. */
  max-height: min(420px, calc(100vh - 160px));
  overflow-y: auto;
  padding-bottom: 8px;
`

const GroupHeading = styled.div`
  padding: 12px 20px 4px;
  background-color: ${({ theme }) => hsla(theme.colors.text, 0.02)};
`

const EmptyState = styled.div`
  padding: 32px 20px;
`
